import { useEffect } from 'react';

/**
 * A tab left open across a deploy keeps running the old bundle: the data it fetches is new, but the code
 * reading it is not, so a fix to how a row is drawn never reaches it (Dave, 2026-09-29, a row that #72 had
 * fixed still drawn the old way). The page checks which bundle is live and reloads itself onto it.
 */
export const VERSION_CHECK_MS = 60_000;

const BUNDLE = /\/assets\/index-[^"']+\.js/;

/** The bundle this page is running, from its own module script tag. Undefined in dev (no hashed bundle). */
export function loadedBundle(doc: Document = document): string | undefined {
  const src = doc.querySelector('script[type="module"][src]')?.getAttribute('src') ?? '';
  return BUNDLE.exec(src)?.[0];
}

/** The bundle the live index.html names, fetched past every cache. Undefined when it can't be read. */
export async function liveBundle(fetchFn: typeof fetch = fetch): Promise<string | undefined> {
  try {
    const res = await fetchFn('/', { cache: 'no-store' });
    if (!res.ok) return undefined;
    return BUNDLE.exec(await res.text())?.[0];
  } catch {
    return undefined;
  }
}

/** True when a different bundle is live than the one running - the only case that reloads. */
export async function isStale(running: string | undefined, fetchFn: typeof fetch = fetch): Promise<boolean> {
  if (!running) return false;
  const live = await liveBundle(fetchFn);
  return live !== undefined && live !== running;
}

/**
 * Reload onto a new bundle, checked every VERSION_CHECK_MS and whenever the tab comes back into view - a
 * hidden tab is not reloaded under anyone, and one brought back to the front catches up at once.
 */
export function useReloadOnNewVersion(reload: () => void = () => window.location.reload()): void {
  useEffect(() => {
    const running = loadedBundle();
    if (!running) return undefined;
    const check = async () => {
      if (document.visibilityState !== 'visible') return;
      // Visibility again after the fetch: the tab may have gone to the background while it was in flight.
      if ((await isStale(running)) && document.visibilityState === 'visible') reload();
    };
    const timer = window.setInterval(() => { void check(); }, VERSION_CHECK_MS);
    const onVisible = () => { void check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [reload]);
}
